import { createStore } from 'vuex';

export default createStore({
  state: {
    routeData: {},
    vehicleData: {},
    additionalData: {},
    finalData: {},
    quoteUniqueId: '',
    reservationType: '',
    documentId: '',
    checkout: {
      transport: {},
      pickup: {},
      delivery: {}
    },

    timerId: null,
    customerInfo: {},
    message_info: {
      phone: '',
      name: '',
      consent: false,
    }
  },
  mutations: {
    SAVE_GET_YOUR_QUOTE_MUTATION(state, data) {
      state.message_info.phone = data.phone;
      state.message_info.name = data.name;
      state.message_info.consent = data.consent;

    },
    SET_CUSTOMER_INFO(state, payload) {
      state.customerInfo = payload;
    },
    SET_TIMER_ID(state, timerId) {
      state.timerId = timerId;
    },
    CLEAR_TIMER_ID(state) {
      state.timerId = null;
    },
    SAVE_ROUTE_STEP_MUTATION(state, data) {
      state.routeData = data;
    },
    SAVE_VEHICLE_STEP_MUTATION(state, data) {
      state.vehicleData = data;
    },
    SAVE_ADDITIONAL_STEP_MUTATION(state, data) {
      state.additionalData = data;
    },
    async PROCESS_DATA_MUTATION(state) {
      try {
        // Prepare data for backend API
        const requestData = {
          routeData: state.routeData,
          vehicleData: state.vehicleData,
          additionalData: state.additionalData
        };

        // Call backend calculator API
        if (!process.env.VUE_APP_CALCULATOR_API_KEY) {
          throw new Error('Calculator API key is not configured');
        }

        const apiBaseUrl = process.env.VUE_APP_API_BASE_URL || '';
        const response = await fetch(`${apiBaseUrl}/api/calculate-price`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': process.env.VUE_APP_CALCULATOR_API_KEY
          },
          body: JSON.stringify(requestData)
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(`Backend calculation failed: ${errorData.message || response.statusText}`);
        }

        const result = await response.json();
        
        if (!result.success) {
          throw new Error(`Calculation error: ${result.message}`);
        }

        // Format the backend response for display
        const backendData = result.data;
        state.finalData = {
          totalPrice: new Intl.NumberFormat('en-US').format(backendData.totalPrice.toFixed(2)),
          totalPriceWithoutDiscount: new Intl.NumberFormat('en-US').format(backendData.totalPriceWithoutDiscount.toFixed(2)),
          oneToFiveDaysPrice: new Intl.NumberFormat('en-US').format(backendData.oneToFiveDaysPrice.toFixed(2)),
          oneToThreeDaysPrice: new Intl.NumberFormat('en-US').format(backendData.oneToThreeDaysPrice.toFixed(2)),
          oneToTenDaysPrice: new Intl.NumberFormat('en-US').format(backendData.oneToTenDaysPrice.toFixed(2))
        };

      } catch (error) {
        console.error('Error calculating price:', error);
        
        // Fallback to show error or default values
        state.finalData = {
          totalPrice: 'Error',
          totalPriceWithoutDiscount: 'Error',
          oneToFiveDaysPrice: 'Error',
          oneToThreeDaysPrice: 'Error',
          oneToTenDaysPrice: 'Error',
          error: error.message
        };
      }
    },
    SAVE_QUOTE_ID_MUTATION(state, data) {
      state.quoteUniqueId = data;
    },
    CLEAR_STORE_MUTATION(state) {
      state.routeData = {};
      state.vehicleData = {};
      state.additionalData = {};
      state.finalData = {};
      state.quoteUniqueId = '';
      state.documentId = '';
      state.reservationType = '';
      state.checkout = {
        transport: {},
        pickup: {},
        delivery: {}
      };
    },
    SAVE_RESERVATION_TYPE_MUTATION(state, data) {
      state.reservationType = data;
    },
    SAVE_CHECKOUT_TRANSPORT_MUTATION(state, data) {
      state.checkout.transport = data;
    },
    SAVE_CHECKOUT_PICKUP_MUTATION(state, data) {
      state.checkout.pickup = data;
    },
    SAVE_CHECKOUT_DELIVERY_MUTATION(state, data) {
      state.checkout.delivery = data;
    },
    CHECKOUT_SAVE_DATA_TO_STORE_MUTATION(state, data) {
      const { routeData, vehicleData, additionalData, finalData, quoteUniqueId, reservationType, checkout } = data;
      state.routeData = routeData;
      state.vehicleData = vehicleData;
      state.additionalData = additionalData;
      state.finalData = finalData;
      state.quoteUniqueId = quoteUniqueId;
      state.reservationType = reservationType;
      state.checkout = checkout;
    },
    SAVE_DOCUMENT_ID_MUTATION(state, data) {
      state.documentId = data;
    }
  },
  actions: {
    SAVE_GET_YOUR_QUOTE_ACTION({ commit }, data) {
      commit('SAVE_GET_YOUR_QUOTE_MUTATION', data);
    },
    SAVE_CUSTOMER_INFO_ACTION({ commit }, payload) {
      commit('SET_CUSTOMER_INFO', payload);
    },
    setTimerId({ commit }, timerId) {
      commit('SET_TIMER_ID', timerId);
    },
    clearTimerId({ commit }) {
      commit('CLEAR_TIMER_ID');
    },
    SAVE_ROUTE_STEP_ACTION({commit}, data) {
      commit('SAVE_ROUTE_STEP_MUTATION', data);
    },
    SAVE_VEHICLE_STEP_ACTION({commit}, data) {
      commit('SAVE_VEHICLE_STEP_MUTATION', data);
    },
    SAVE_ADDITIONAL_STEP_ACTION({commit}, data) {
      commit('SAVE_ADDITIONAL_STEP_MUTATION', data);
      commit('PROCESS_DATA_MUTATION');
    },
    SAVE_QUOTE_ID_ACTION({commit}, data) {
      commit('SAVE_QUOTE_ID_MUTATION', data);
    },
    CLEAR_STORE_ACTION({commit}) {
      commit('CLEAR_STORE_MUTATION');
    },
    SAVE_RESERVATION_TYPE_ACTION({commit}, data) {
      commit('SAVE_RESERVATION_TYPE_MUTATION', data);
    },
    SAVE_CHECKOUT_TRANSPORT_ACTION({commit}, data) {
      commit('SAVE_CHECKOUT_TRANSPORT_MUTATION', data);
    },
    SAVE_CHECKOUT_PICKUP_ACTION({commit}, data) {
      commit('SAVE_CHECKOUT_PICKUP_MUTATION', data);
    },
    SAVE_CHECKOUT_DELIVERY_ACTION({commit}, data) {
      commit('SAVE_CHECKOUT_DELIVERY_MUTATION', data);
    },
    CHECKOUT_SAVE_DATA_TO_STORE_ACTION({commit}, data) {
      commit('CHECKOUT_SAVE_DATA_TO_STORE_MUTATION', data);
    },
    SAVE_DOCUMENT_ID_ACTION({commit}, data) {
      commit('SAVE_DOCUMENT_ID_MUTATION', data);
    }
  }
});

/* FUNCTIONS */

async function fetchPricing() {
  const res = await fetch('https://ratesautotransport.com/api/pricing');
  const pricingBands = await res.json();
  return pricingBands;
}

async function distancePrice(distance) {
  const pricingBands = await fetchPricing();
  for (let band of pricingBands) {
    if (distance >= band.min && distance <= band.max) {
      return distance * band.price;
    }
  }
  return null; // or some default value
}